import { AfterViewInit, Component, DestroyRef, ElementRef, inject, Input, OnDestroy, OnInit, ViewChild, } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatDrawer } from '@angular/material/sidenav';
import { ActivatedRoute, Router } from '@angular/router';
import * as MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import { Store } from '@ngrx/store';
import * as turf from '@turf/turf';
import { cloneDeep, intersectionBy } from 'lodash';
import * as mapboxgl from 'mapbox-gl';
import { GeoJSONSource, LngLat, Popup, VectorSource } from 'mapbox-gl';
import { forkJoin, fromEvent, throwError } from 'rxjs';
import { catchError, debounceTime, finalize, skipWhile, take, timeout } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';

import { ToastLevel } from 'src/app/shared/models/toast-message.module';
import { ToastService } from 'src/app/shared/services/toast.service';

import { environment } from '../../../../environments/environment';
import { CohortService } from '../../../core/api/cohort.service';
import { GisService } from '../../../core/api/gis.service';
import { IneligibleAreaUserService } from '../../../core/api/ineligible-area-user.service';
import { KmlForestryUploadService } from '../../../core/api/kml-forestry-upload.service';
import { RegionService } from '../../../core/api/region.service';
import { SiteService } from '../../../core/api/site.service';
import { buildAddressLine } from '../../../core/helper/addresses';
import { MapService } from '../../../core/helper/map.service';
import { SiteHelperService } from '../../../core/helper/site.service';
import { DialogComingSoonComponent } from '../../../shared/dialogs/dialog-coming-soon/dialog-coming-soon.component';
import { DialogIneligibleParcelComponent } from '../../../shared/dialogs/dialog-ineligible-parcel/dialog-ineligible-parcel.component';
import { DialogYesNoComponent } from '../../../shared/dialogs/dialog-yes-no/dialog-yes-no.component';
import { Buyer, Cohort, Parcel, Site, User } from '../../../shared/models';
import * as fromApp from '../../../store';
import { selectAppConfigState } from '../../../store/app-config/app-config.selectors';
import { selectCurrentUser } from '../../../store/auth/auth.selectors';
import { MapControlState } from '../../../store/map-control/map-control.reducer';
import { selectMapControlState } from '../../../store/map-control/map-control.selectors';
import { setSelectedParcels } from '../../../store/parcel/parcel.actions';
import { MapCenter, ParcelState } from '../../../store/parcel/parcel.reducer';
import { selectEmail, selectLocationType, selectParcelsLocked, selectParcelState, selectSelectedParcels, } from '../../../store/parcel/parcel.selectors';
import { setActiveSiteParcelsAndAvailableCohorts, setActiveSiteSelectedParcels, setAvailableCohorts, } from '../../../store/site/site.actions';
import { selectReadOnlyMapInfo } from '../../../store/site/site.selectors';
import { toggleMapControlsKMLUpload } from "../../../store/map-control/map-control.actions";
import { FeatureCollection, Geometry } from "geojson";

const POPUP_STYLES = {
  title:
    'style="font-family: Roboto; font-style: normal; font-weight: bold; font-size: 18px; line-height: 21px; letter-spacing: 0.005em; color: #424242; margin-block-start: 0.75em; margin-block-end: 0.75em;"',
  border: 'style="border: 0.5px solid #888888; margin-bottom: 5px;"',
  details:
    'style="font-family: Roboto; color: #424242; font-style: normal; font-weight: normal; font-size: 14px; line-height: 20px; letter-spacing: 0.005em; margin-block-start: 0; margin-block-end: 0;"',
  button:
    'style="background: #58BA2A; border: 1px solid #58BA2A; box-sizing: border-box; border-radius: 3px; width: 100%; height: 34px; font-family: Roboto; font-style: normal; font-weight: normal; font-size: 14px; line-height: 20px; text-align: center; letter-spacing: 1.5px; color: white; margin-top: 0.75em; margin-bottom: 0.75em;"',
};

export interface ParcelCohorts {
  llUuid: string;
  cohorts: Cohort[];
}

@Component({
  selector: 'fc-locate-map',
  templateUrl: './locate-map.component.html',
  styleUrls: ['./locate-map.component.scss'],
})
export class LocateMapComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('documentUpload', { static: false }) documentUpload: ElementRef;
  @Input() drawer: MatDrawer;
  @Input() activeSite: Site;

  destroyRef = inject(DestroyRef);

  map: mapboxgl.Map;
  readOnlyMap: boolean;
  isAdmin: boolean;
  style = environment.mapbox.baseMap;
  contactEmail = '';
  helpEmail = '';
  locationCoordinates: number[];
  siteId: number;

  initialHighlightFilter: string[];
  parcels: Array<Parcel> = [];
  mapCoordinates: MapCenter = null;
  forestryKmlGeoJson: any = null;
  email: string = null;

  maxParcelsPerSite = 25;
  locationType: string = null;

  standPopup: Popup;

  editingSavedSite = false;

  mapControlState: MapControlState;

  currentUser: User;
  selectedBuyer: Buyer = null;

  allCohorts: ParcelCohorts[] = [];
  parcelCohorts: Cohort[] = [];
  availableCohorts: Cohort[] = [];
  availableCohortsByBuyer: Cohort[] = [];

  parcelSelectLock = false;
  showZoomInNote: boolean = false;
  hoverToolTip = new mapboxgl.Popup({
    closeButton: false,
    className: 'layer-data',
  });
  hoverParcelIds = [];
  currentMarker: mapboxgl.Marker;

  constructor(
    readonly store: Store<fromApp.AppState>,
    readonly route: ActivatedRoute,
    readonly router: Router,
    readonly cohortService: CohortService,
    readonly regionService: RegionService,
    readonly ineligibleAreaUserService: IneligibleAreaUserService,
    readonly siteService: SiteService,
    readonly siteHelperService: SiteHelperService,
    readonly mapService: MapService,
    readonly gisService: GisService,
    readonly kmlForestryUploadService: KmlForestryUploadService,
    public dialog: MatDialog,
    readonly toastService: ToastService
  ) {
    this.siteId = +this.route.snapshot.params.id;
  }

  ngOnInit() {
    forkJoin([
      this.store.select(selectAppConfigState).pipe(
        timeout(10000),
        skipWhile(appConfig => appConfig.config.length === 0),
        take(1)
      ),
      this.store.select(selectCurrentUser).pipe(
        timeout(10000),
        skipWhile(currentUser => currentUser === null),
        take(1)
      ),
      this.store.select(selectLocationType).pipe(take(1)),
      this.store.select(selectEmail).pipe(take(1)),
      this.store.select(selectParcelState).pipe(take(1)),
      this.store.select(selectReadOnlyMapInfo).pipe(take(1)),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ([appConfig, currentUser, locationType, email, parcelState, readOnlyInfo]) => {
          this.maxParcelsPerSite = appConfig.config.max_parcels_per_site
            ? parseInt(appConfig.config.max_parcels_per_site, 10)
            : 25;

          this.contactEmail = appConfig.config.fc_contact_email;
          this.helpEmail = appConfig.config.fc_help_email;
          this.activeSite = readOnlyInfo.activeSite;
          this.currentUser = currentUser;
          this.selectedBuyer = this.currentUser?.preferredBuyer;

          this.locationType = locationType;

          this.email = email;

          this.readOnlyMap = !this.siteHelperService.canSaveMap(readOnlyInfo);
          this.isAdmin = readOnlyInfo.isAdmin;
          if (this.map && this.readOnlyMap) {
            this.map.off('click', e => {
              this.selectParcelByLatLng(e.lngLat);
            });
          }

          // Parcel Select
          // TODO: This delay didn't work when returning from the edit map, re-added the setTimeOut 0 to put it at the end
          //  of the queue.  We should figure out another way (e.g. a map event)
          setTimeout(() => {
            this.mapCoordinates = { ...parcelState.mapCenter };
            this.editingSavedSite = false;
            const featureCollection = {
              type: 'FeatureCollection',
              features: null,
            };
            if (this.mapCoordinates.lat === null || this.mapCoordinates.lng === null) {
              if (parcelState.selectedParcels.length > 0) {
                featureCollection.features = parcelState.selectedParcels.map(parcel => parcel.feature);
                const centerCoords = turf.centroid(featureCollection);
                this.mapCoordinates = {
                  lat: centerCoords.geometry.coordinates[1],
                  lng: centerCoords.geometry.coordinates[0],
                };
                this.editingSavedSite = true;
              }
            }

            if (
              (this.mapCoordinates.lat === null || this.mapCoordinates.lng === null) &&
              (this.activeSite.initialLat === null || this.activeSite.initialLng === null)
            ) {
              // Bail back to dashboard
              this.navigateMapDataNull();
            } else {
              // Use initial lat/lng from site
              if (this.mapCoordinates.lat === null || this.mapCoordinates.lng === null) {
                this.mapCoordinates = {
                  lat: this.activeSite.initialLat,
                  lng: this.activeSite.initialLng,
                };
              }

              // Build initial highlight filter and set parcels array
              this.initialHighlightFilter = this.buildFilter(parcelState.selectedParcels);
              this.parcels = this.parcels.concat(parcelState.selectedParcels);

              this.forestryKmlGeoJson = parcelState.forestryKml;

              this.initializeMap();
              this.disableFeatures();
              this.buildMap();
              this.addMapListeners();
              this.evaluateAllCohorts();
            }
          }, 1000);
        },
      });

    this.store.select(selectMapControlState).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      mapControls => {
        this.mapControlState = mapControls;
      }
    );

    this.store
      .select(selectParcelsLocked)
      .pipe(
        skipWhile(() => this.activeSite === null),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: parcelSelectLock => {
          this.parcelSelectLock = parcelSelectLock;
        },
      });

    // watch the list of selected parcels for any removed outside of this map
    this.store
      .select(selectSelectedParcels)
      .pipe(
        skipWhile(() => this.activeSite === null),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: parcels => {
          if (this.parcels.length > 0) {
            const newIds = new Set(parcels.map(obj => obj.llUuid));
            const removedParcels = this.parcels.filter(parcel => !newIds.has(parcel.llUuid));
            if (removedParcels.length > 0) {
              removedParcels.forEach(parcel => {
                this.removeParcel(parcel);
              });
              const geojsonSource = this.map.getSource('selected-parcels') as GeoJSONSource;
              geojsonSource.setData({
                type: 'FeatureCollection',
                features: parcels.map(s => s.feature),
              });
            }
          }
        },
      });
  }

  ngAfterViewInit() {
    const reverseGeocoder = new MapboxGeocoder({
      accessToken: environment.mapbox.accessToken,
      localGeocoder: this.coordinatesGeocoder,
      placeholder: 'Search by Address or Lat/Long',
      countries: 'us',
      reverseGeocode: true,
    });

    reverseGeocoder.addTo('.reverse-location-container');

    reverseGeocoder.on('result', item => this.checkRegionAndProceed(item));
    reverseGeocoder.on('clear', () => {
      if (this.currentMarker) {
        this.currentMarker.remove();
      }
    });

    this.drawer.openedChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: opened => {
        if (!opened) {
          this.map.resize();
        }
      },
    });

    fromEvent(window, 'resize')
      .pipe(debounceTime(250), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.map.resize();
      });
  }

  ngOnDestroy() {
    if (this.map) {
      this.map.remove();
    }
  }

  navigateMapDataNull(): void {
    let url = '/start-site';

    if (this.currentUser) {
      if (this.currentUser.isAdmin) {
        url = '/admin/dashboard';
      } else {
        url = '/account/dashboard';
      }
    }

    this.router.navigateByUrl(url);
  }

  checkRegionAndProceed(item: any): void {
    this.locationCoordinates = item.result.center;
    this.regionService.getRegionsByLatLong(this.locationCoordinates[1], this.locationCoordinates[0]).subscribe({
      next: allRegions => {
        if (!allRegions || allRegions.length === 0) {
          this.openComingSoonDialog(item.result.place_name);
        }
        this.map.setCenter({
          lat: this.locationCoordinates[1],
          lng: this.locationCoordinates[0],
        });
        // Add pin to map as well
        if (this.currentMarker) {
          this.currentMarker.remove();
        }
        this.currentMarker = new mapboxgl.Marker({ color: '#47ab1f' })
          .setLngLat([this.locationCoordinates[0], this.locationCoordinates[1]]) // Longitude and latitude of the marker
          .addTo(this.map); // Add the marker to the map
      },
    });
  }

  openComingSoonDialog(addressStr: string): void {
    const dialogRef = this.dialog.open(DialogComingSoonComponent, {
      panelClass: 'fc-coming-soon-dialog',
      data: { email: this.email },
      autoFocus: false,
    });

    dialogRef.afterClosed().subscribe(result => {
      // only save user if the hiddenCheck field is untouched
      if (result.hiddenCheck === null && result.email !== null) {
        this.store
          .select(selectParcelState)
          .pipe(take(1), takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: (parcelState: ParcelState) => {
              const siteAcres = parcelState.acres;
              const latLng = parcelState.mapCenter.lat + ', ' + parcelState.mapCenter.lng;
              const ineligibleAreaUser = {
                email: result.email,
                address: addressStr,
                coordinates: latLng,
                parcel_id: null,
                site_size: siteAcres,
                referrer_code: this.selectedBuyer?.referrerCode,
                state: '',
                county: '',
              };
              this.ineligibleAreaUserService.createIneligibleAreaUser(ineligibleAreaUser).subscribe(
                result => {},
                () => console.log('ERROR creating ineligible area user'),
                () => {}
              );
            },
          });
      }
    });
  }

  buildFilter(parcels: Parcel[]): string[] {
    return parcels.reduce(
      (polygon, parcel) => {
        polygon.push(String(parcel.llUuid));
        return polygon;
      },
      ['in', 'llUuid']
    );
  }

  coordinatesGeocoder(query: string): RegExpMatchArray | any[] {
    // match anything which looks like a decimal degrees coordinate pair
    const matches = /^\s*(?:Lat: )?(-?\d+\.?\d*)[, ]+(?:Lng: )?(-?\d+\.?\d*)\s*$/.exec(query);
    if (!matches) {
      return null;
    }

    function coordinateFeature(lng, lat) {
      return {
        center: [lng, lat],
        geometry: {
          type: 'Point',
          coordinates: [lng, lat],
        },
        place_name: 'Lat: ' + lat + ' Lng: ' + lng,
        place_type: ['coordinate'],
        properties: {},
        type: 'Feature',
      };
    }

    const coord1 = Number(matches[1]);
    const coord2 = Number(matches[2]);
    const geocodes = [];

    if (coord1 < -90 || coord1 > 90) {
      // must be lng, lat
      geocodes.push(coordinateFeature(coord1, coord2));
    }

    if (coord2 < -90 || coord2 > 90) {
      // must be lat, lng
      geocodes.push(coordinateFeature(coord2, coord1));
    }

    if (geocodes.length === 0) {
      // else could be either lng, lat or lat, lng
      geocodes.push(coordinateFeature(coord1, coord2));
      geocodes.push(coordinateFeature(coord2, coord1));
    }

    return geocodes;
  }

  initializeMap(): void {
    (mapboxgl as any).accessToken = environment.mapbox.accessToken;

    // if a kml file has been uploaded, load map showing entire kml region
    // else, load with default zoom level and center coordinates
    if (this.forestryKmlGeoJson?.length > 0) {
      const featureCollection = turf.featureCollection(this.forestryKmlGeoJson);
      const boundingBox = turf.bbox(featureCollection);
      const [x1, y1, x2, y2] = boundingBox;
      this.map = new mapboxgl.Map({
        container: 'parcel-map',
        style: this.style,
        bounds: [x1, y1, x2, y2],
      });
    } else {
      let lng = this.mapCoordinates.lng;
      let lat = this.mapCoordinates.lat;
      this.map = new mapboxgl.Map({
        container: 'parcel-map',
        style: this.style,
        zoom: 14,
        center: [lng, lat],
      });
    }
    this.mapService.currentMap = this.map;
    this.showZoomInNote = this.map.getZoom() < 12.0;

    // Add map controls
    this.map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-left');
    // if there are parcels selected center on them instead of the
    // original position
    if (this.parcels && this.parcels.length > 0) {
      const boundingBox = turf.bbox({
        type: 'FeatureCollection',
        features: this.parcels.map(parcel => parcel.feature),
      }) as any;
      this.map.fitBounds(boundingBox, {
        padding: 200,
        animate: true,
      });
    }
  }

  disableFeatures(): void {
    // Disable default box zooming.
    this.map.boxZoom.disable();

    // disable map rotation using right click + drag
    // this.map.dragRotate.disable();

    // disable map rotation using touch rotation gesture
    // this.map.touchZoomRotate.disableRotation();

    // Disable default drag zooming when the shift key is held down.
    // this.map.dragPan.disable();
  }

  getHoverParcelHTML(features): string {
    let html = '<div class="map-popup">';
    html += '<div class="popup-title">Parcel Data</div>';
    features.forEach(feature => {
      html += '<div class="popup-border"></div>';
      html +=
        '<div class="popup-details">Owner: ' +
        (typeof feature.properties.owner !== 'undefined' ? feature.properties.owner : '') +
        '</div>';
      html += '<div class="popup-details">County: ' + feature.properties.county + '</div>';
      html += '<div class="popup-details">State: ' + feature.properties.state2 + '</div>';
      html += '<div class="popup-details">Parcel #: ' + feature.properties.parcelnumb + '</div>';
      html += '<div class="popup-details">Address: ' + buildAddressLine(feature.properties) + '</div>';
      html += '<div class="popup-details">GID: ' + feature.properties.gid + '</div>';
    });
    html += '</div>';
    return html;
  }

  buildMap(): void {
    this.mapService.loadImages();
    this.map.on('mousemove', 'parcel-data', e => {
      if (e.features.length > 0) {
        if (this.hoverParcelIds !== e.features) {
          let ids = [];
          e.features.forEach(feature => {
            ids.push(feature.properties.ll_uuid);
          });
          this.map.setFilter('parcel-data-hover', ['in', 'll_uuid', ...ids]);
          this.hoverToolTip.setHTML(this.getHoverParcelHTML(e.features)).setMaxWidth('none');
        }

        if (this.hoverParcelIds.length == 0 && this.currentUser.isAdmin) {
          this.hoverToolTip.setLngLat(e.lngLat);
          this.hoverToolTip.addTo(this.map);
        }

        this.hoverParcelIds = e.features;
      }
    });

    this.map.on('mouseenter', 'parcel-data', () => {
      this.map.getCanvas().style.cursor = this.readOnlyMap || this.parcelSelectLock ? '' : 'pointer';
    });
    this.map.on('mouseleave', 'parcel-data', () => {
      this.map.getCanvas().style.cursor = 'not-allowed';
      this.map.setFilter('parcel-data-hover', ['in', 'll_uuid']);
      this.hoverToolTip.remove();
      this.hoverParcelIds = [];
    });
    this.map.on('mouseenter', 'unavailable_area', () => {
      this.map.getCanvas().style.cursor = 'not-allowed';
    });
    this.map.on('mouseleave', 'unavailable_area', () => {
      this.map.getCanvas().style.cursor = this.readOnlyMap || this.parcelSelectLock ? '' : 'pointer';
    });
    this.map.on('zoomend', e => {
      this.showZoomInNote = e.target.getZoom() < 12.0;
    });

    this.map.on('load', () => {
      this.map.addSource('parcels', {
        type: 'vector',
        tiles: [environment.tileServerUrl + '/maps/layers/{z}/{x}/{y}.vector.pbf'],
        tolerance: 0,
      } as VectorSource);

      this.map.addLayer({
        id: 'unavailable_area',
        type: 'fill',
        source: 'parcels',
        'source-layer': 'unavailable_area',
        layout: {
          visibility: 'visible',
        },
        paint: {
          'fill-color': 'red',
          'fill-opacity': 0.5,
        },
      });

      this.map.addLayer({
        id: 'waterways-data1',
        type: 'fill',
        source: 'parcels',
        'source-layer': 'waterway',
        paint: {
          'fill-antialias': false,
          'fill-color': '#2D5FF5',
          'fill-opacity': 0.5,
        },
        filter: ['==', '$type', 'Polygon'],
        minzoom: 12,
      });

      this.map.addLayer({
        id: 'waterways-data2',
        type: 'line',
        source: 'parcels',
        'source-layer': 'waterway',
        paint: {
          'line-color': '#2D5FF5',
          'line-width': 2,
        },
        filter: ['==', '$type', 'LineString'],
        minzoom: 12,
      });


      this.map.addLayer({
        id: 'parcel-data',
        type: 'fill',
        source: 'parcels',
        'source-layer': 'parcel',
        paint: {
          'fill-outline-color': 'rgba(255,127,80,0.9)',
          'fill-color': 'rgba(0,0,0,0.0)',
        },
      });

      this.map.addLayer({
        id: 'parcel-data-outline',
        type: 'line',
        source: 'parcels',
        'source-layer': 'parcel',
        paint: {
          'line-color': '#FFFFFF',
          'line-width': 1,
        },
      });

      this.map.addLayer({
        id: 'parcel-data-hover',
        type: 'fill',
        source: 'parcels',
        'source-layer': 'parcel',
        paint: {
          'fill-outline-color': '#F5BD02',
          'fill-color': '#F5BD02',
          'fill-opacity': 0.5,
        },
        filter: ['in', 'll_uuid'],
      });
      this.map.addSource('selected-parcels', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: this.parcels.map(s => s.feature),
        },
      });
      this.map.addLayer({
        id: 'parcel-data-outline2',
        type: 'fill',
        source: 'selected-parcels',
        paint: {
          'fill-outline-color': '#F5BD02',
          'fill-color': '#F5BD02',
          'fill-opacity': 0.75,
        },
      });
      if (this.forestryKmlGeoJson?.length > 0) {
        const newCollection = turf.featureCollection(this.forestryKmlGeoJson);
        const geojsonCollection = newCollection as unknown as FeatureCollection<Geometry, { [key: string]: any }>;

        this.map.addSource(`kml-import`, {
          type: 'geojson',
          data: geojsonCollection,
        });

        this.map.addLayer({
          id: `kml-import`,
          type: 'fill',
          source: `kml-import`,
          layout: {
            visibility: 'visible',
          },
          paint: {
            'fill-color': 'rgba(27, 229, 204, 0.3)', // '#6e599f',
          },
        });
        this.map.addLayer({
          id: `kml-import-outline`,
          type: 'line',
          source: `kml-import`,
          paint: {
            'line-color': 'rgba(27, 229, 204, 0.7)',
            'line-width': 2,
          },
        });

        if (!this.readOnlyMap) {
          this.dialog.open(DialogYesNoComponent, {
            panelClass: 'fc-yes-no-dialog',
            data: {
              title: 'KML/KMZ Imported',
              line1: 'Your KML/KMZ file has been imported and is shown on the map.',
              line2: 'Please select any additional parcels that you wish to include in your site.',
              buttonTextYes: 'Continue',
            },
          });
        }
        this.store.dispatch(toggleMapControlsKMLUpload());
      }


      this.map.once('idle', () => {
        this.drawer.toggle();
      });
      this.mapService.setLayerVisibility(this.mapControlState);
    });

    if (!this.editingSavedSite) {
      this.selectUserSpecifiedParcel();
    }
  }

  selectUserSpecifiedParcel(): void {
    if (this.locationType && this.locationType !== 'address') {
      this.map.once('idle', () => {
        // Check if there are parcels, so we aren't doing this on returning and de-selecting the parcel
        if (this.parcels && this.parcels.length === 0) {
          this.selectParcelByLatLng(new LngLat(this.mapCoordinates.lng, this.mapCoordinates.lat));
        }
      });
    }
  }

  selectParcelByLatLng(point: LngLat): void {
    this.gisService.getParcelsByLatLng(point.lat, point.lng).subscribe({
      next: (parcels: Parcel[]) => {
        if (parcels && parcels.length > 0) {
          parcels.forEach(newParcel => {
            {
              const featureIndex = this.parcels.findIndex(parcel => parcel.llUuid === newParcel.llUuid);
              if (featureIndex !== -1) {
                this.removeParcel(newParcel);
              } else {
                this.selectParcelByParcel(newParcel);
              }
            }
          });
        } else if (this.map.getZoom() >= 12) {
          this.openParcelNotAvailableDialog();
        }
      },
      error: catchError(err => {
        return throwError(err);
      }),
    });
  }

  removeParcel(parcel: Parcel) {
    // remove parcel from parcel list
    this.updateParcelData(parcel);
    // remove parcel cohorts from allCohorts and reevaluate availableCohorts
    this.removeParcelFromAllCohorts(parcel.llUuid);
    this.evaluateAvailableCohorts();
  }

  selectParcelByParcel(newParcel: Parcel) {
    const minimumMet = this.minimumParcelAcreageMet(newParcel);

    if (this.parcels.length === 0 || minimumMet) {
      let newParcelCohorts: Cohort[] = [];

      this.parcelSelectLock = true;

      const parcelCenter = turf.centroid(newParcel.feature).geometry.coordinates;
      this.cohortService
        .getCohortsByLatLong(parcelCenter[1], parcelCenter[0])
        .pipe(finalize(() => (this.parcelSelectLock = false)))
        .subscribe({
          next: (cohorts: Cohort[] | null) => {
            newParcelCohorts = cohorts;

            this.allCohorts.push({
              llUuid: newParcel.llUuid,
              cohorts: newParcelCohorts,
            });
            this.evaluateAvailableCohorts();

            if (this.selectedBuyer) {
              const parcelCohortsByBuyer = this.parcelCohorts.filter(pc => pc.buyer.id === this.selectedBuyer.id);
              // parcel is available to the buyer's cohort and the cohort is the same as all selected previous parcels
              if (this.availableCohortsByBuyer.length > 0) {
                this.updateParcelData(newParcel);
              } else if (this.availableCohorts.length === 0 && parcelCohortsByBuyer.length > 0) {
                // if this parcel has the same buyer but not the same cohorts, show dialog to create another site
                this.removeParcelFromAllCohorts(newParcel.llUuid);
                this.openNewSiteDialog();
              } else {
                // if this parcel doesn't have any available cohorts, show not eligible dialog to be contacted
                this.removeParcelFromAllCohorts(newParcel.llUuid);
                const dialogRef = this.dialog.open(DialogIneligibleParcelComponent, {
                  panelClass: 'fc-dialog-ineligible-parcel',
                  data: {},
                  autoFocus: false,
                });

                dialogRef.afterClosed().subscribe({
                  next: result => {
                    // only save user if the hiddenCheck field is untouched
                    if (result.hiddenCheck === null) {
                      this.store
                        .select(selectParcelState)
                        .pipe(take(1), takeUntilDestroyed(this.destroyRef))
                        .subscribe(parcelState => {
                          const siteAcres = parcelState.acres;
                          const ineligibleAreaUser = {
                            email: result.email,
                            address: null,
                            coordinates: parcelCenter[1] + ',' + parcelCenter[0],
                            parcel_id: `${newParcel.id}`,
                            site_size: siteAcres,
                            referrer_code: this.selectedBuyer?.referrerCode,
                            state: '',
                            county: '',
                          };
                          return this.ineligibleAreaUserService
                            .createIneligibleAreaUser(ineligibleAreaUser)

                            .subscribe(
                              result => {},
                              () => console.log('ERROR creating ineligible area user'),
                              () => {}
                            );
                        });
                    }
                  },
                });
              }
            } else if (
              (this.activeSite?.cohort?.buyer && this.availableCohortsByBuyer.length > 0) || // if this parcel has at least one available cohort & buyer that is in availableCohorts
              (!this.activeSite?.cohort?.buyer && this.availableCohorts.length > 0)
            ) {
              // or there is no chosen buyer and at least one available cohort, continue to add parcel
              this.updateParcelData(newParcel);
            } else {
              // if this parcel doesn't have any intercepting cohorts/buyers, show dialog to create another site
              this.removeParcelFromAllCohorts(newParcel.llUuid);
              this.openNewSiteDialog();
            }
          },
        });
    } else {
      if (this.standPopup && this.standPopup.isOpen()) {
        this.standPopup.remove();
      }

      let html = '<div>';
      html += '<div ' + POPUP_STYLES.title + '>Invalid Selection';
      html += '</div>';
      html += '<div ' + POPUP_STYLES.border + '></div>';
      html +=
        '<p ' +
        POPUP_STYLES.details +
        '>If you would like to add a parcel to your Carbon Site, it must be at least an acre in size.</p>';
      html += '</div>';

      const parcelCenter = turf.centroid(newParcel.feature).geometry.coordinates;
      const lngLat = {
        lng: parcelCenter[0],
        lat: parcelCenter[1],
      };

      this.standPopup = new mapboxgl.Popup().setLngLat(lngLat).setHTML(html).addTo(this.map);
    }
  }

  removeParcelFromAllCohorts(ll_uuid: string): void {
    const parcelIndex = this.allCohorts.findIndex(c => c.llUuid === ll_uuid);
    this.allCohorts.splice(parcelIndex, 1);
  }

  openNewSiteDialog(): void {
    this.dialog.open(DialogYesNoComponent, {
      panelClass: 'fc-yes-no-dialog',
      data: {
        title: "WE'RE SORRY",
        line1:
          "Your selected properties are only available to different buyers. We currently don't support this. Please remove a property and try again. You can create a new site with the other property.",
        buttonTextYes: 'OK',
      },
      autoFocus: false,
    });
  }

  openParcelNotAvailableDialog(): void {
    this.dialog.open(DialogYesNoComponent, {
      panelClass: 'fc-yes-no-dialog',
      data: {
        title: 'UNSERVICEABLE AREA',
        line1: 'You have selected an area where we cannot service due to missing required parcel information.',
        line2: `Please reach out to us at ${this.contactEmail} if you wish to discuss further.`,
        buttonTextYes: 'OK',
      },
      autoFocus: false,
    });
  }

  evaluateAllCohorts(): void {
    this.parcels.forEach((p, i) => {
      const parcelCenter = turf.centroid(p.feature);
      const parcelLng = parcelCenter.geometry.coordinates[0];
      const parcelLat = parcelCenter.geometry.coordinates[1];
      let parcelCohorts: Cohort[] = [];
      this.cohortService.getCohortsByLatLong(parcelLat, parcelLng).subscribe({
        next: (cohorts: Cohort[] | null) => {
          parcelCohorts = cohorts;
          this.allCohorts.push({
            llUuid: p.llUuid,
            cohorts: parcelCohorts,
          });
          if (i + 1 === this.parcels.length) {
            this.evaluateAvailableCohorts();
          }
        },
      });
    });
  }

  evaluateAvailableCohorts(): void {
    const cohorts = [];
    this.parcelCohorts = [];
    if (this.allCohorts.length > 0) {
      this.allCohorts[this.allCohorts.length - 1].cohorts.forEach(c => this.parcelCohorts.push(c));
      this.allCohorts.forEach(c => cohorts.push(c.cohorts));
    }

    if (cohorts.length === 0) {
      this.availableCohorts = [];
      this.availableCohortsByBuyer = [];
    } else {
      // if all parcels are removed and the first is being added, then add it if it has the preferred buyer
      if (this.allCohorts.length === 1) {
        this.availableCohorts = cohorts[0];
      } else {
        let cohortIntersections: Cohort[] = cohorts[0];
        cohorts.forEach((cohortArray, i) => {
          if (i > 0) {
            cohortIntersections = intersectionBy(cohortIntersections, cohortArray, 'id');
          }
        });
        this.availableCohorts = cohortIntersections;
      }

      const filterByBuyer = this.selectedBuyer || (this.activeSite?.buyerSelected && this.activeSite?.cohort?.buyer);
      if (filterByBuyer && this.availableCohorts.length > 0) {
        this.availableCohortsByBuyer = this.availableCohorts.filter(c => c.buyer.id === filterByBuyer.id);
      } else {
        this.availableCohortsByBuyer = this.availableCohorts;
      }
    }

    this.store.dispatch(
      setAvailableCohorts({
        cohorts: cloneDeep(this.availableCohortsByBuyer),
      })
    );
  }

  minimumParcelAcreageMet(newParcel: Parcel): boolean {
    const acreage = turf.area(newParcel.feature) * 0.000247105;
    return acreage > 1;
  }

  /**
   * Updates active site's parcels and possibly active site itself in state
   * @param {boolean} updateActiveSite - Flag that indicates if active site needs to be updated in state
   */
  updateParcelState(updateActiveSite: boolean = false): void {
    if (this.parcels.length <= this.maxParcelsPerSite) {
      // Build and set filter on map
      const geojsonSource = this.map.getSource('selected-parcels') as GeoJSONSource;
      geojsonSource.setData({
        type: 'FeatureCollection',
        features: this.parcels.map(s => s.feature),
      });

      // Save list of selected parcels to state
      if (this.activeSite) {
        if (updateActiveSite) {
          this.store.dispatch(
            setActiveSiteParcelsAndAvailableCohorts({
              siteId: this.activeSite.id,
              parcels: cloneDeep(this.parcels),
              availableCohorts: cloneDeep(this.availableCohorts),
            })
          );
        } else {
          this.store.dispatch(
            setActiveSiteSelectedParcels({
              parcels: cloneDeep(this.parcels),
            })
          );
        }
      }

      this.store.dispatch(
        setSelectedParcels({
          data: cloneDeep(this.parcels),
        })
      );
    }
  }

  /**
   * Adds or removes a given parcel to/from the current site.
   * @param {Parcel} newParcel - The parcel being added or removed.
   * @param {boolean} updateActiveSite - Flag to pass on to next function that indicates if active site needs to be updated in state
   */
  updateParcelData(newParcel: Parcel, updateActiveSite: boolean = false): void {
    const featureIndex = this.parcels.findIndex(parcel => parcel.llUuid === newParcel.llUuid);
    if (featureIndex > -1) {
      this.parcels.splice(featureIndex, 1);
    } else if (this.parcels.length < this.maxParcelsPerSite) {
      this.parcels.push(newParcel);
    } else {
      this.openMaxParcelsDialog();
    }

    this.updateParcelState(updateActiveSite);
  }

  createNewParcel(currentFeatureList: mapboxgl.MapboxGeoJSONFeature[]): Parcel {
    return new Parcel({
      id: null,
      gid: currentFeatureList[0].id,
      siteVersionId: null,
      parcelNumber: currentFeatureList[0].properties.parcelnumb,
      feature: {
        id: currentFeatureList[0].id,
        properties: currentFeatureList[0].properties,
        geometry: currentFeatureList[0].geometry,
        type: currentFeatureList[0].type,
      },
      stands: [],
    });
  }

  addMapListeners(): void {
    this.addParcelSelectListener();
  }

  addParcelSelectListener(): void {
    if (!this.readOnlyMap) {
      this.map.on('click', e => {
        if (!this.parcelSelectLock) {
          this.selectParcelByLatLng(e.lngLat);
        }
      });
    }
  }

  openMaxParcelsDialog(): void {
    this.dialog.open(DialogYesNoComponent, {
      panelClass: 'fc-yes-no-dialog',
      data: {
        title: 'YOU HAVE REACHED THE PARCEL LIMIT',
        line1: `We're sorry - a Carbon Site in our system cannot have more than ${this.maxParcelsPerSite} parcels.`,
        line2: `If you are considering adding more than ${this.maxParcelsPerSite} parcels, please contact us at ${this.helpEmail}`,
        buttonTextYes: 'OK Got it',
        buttonTextNo: null,
      },
      autoFocus: false,
    });
  }

  handleViewStandsClick(): void {
    this.mapService.navigateToMap(
      this.router,
      this.activeSite,
      this.currentUser?.isAdmin || this.currentUser?.isLimitedAdmin ? 'admin' : 'site'
    );
  }

  handleSiteDetailsClick(): void {
    this.router.navigate([
      (this.currentUser?.isAdmin || this.currentUser?.isLimitedAdmin ? 'admin' : 'account') + '/site/' + this.siteId,
    ]);
  }

  openKMLDialog() {
    const dialogRef = this.dialog.open(DialogYesNoComponent, {
      panelClass: 'fc-yes-no-dialog',
      data: {
        title: 'CAUTION - Adding custom KML',
        line1:
          'This will add a custom polygon as a parcel, ignoring any provided data. This should be used sparingly, only when the provided geom data is incorrect.',
        line2: 'Please confirm.',
        buttonTextYes: 'Provide KML',
        buttonTextNo: 'Cancel',
      },
      autoFocus: false,
    });

    dialogRef.afterClosed().subscribe({
      next: result => {
        if (result !== null && result === true) {
          const documentUpload = this.documentUpload.nativeElement;
          documentUpload.onchange = () => {
            if (documentUpload.files.length === 1) {
              const file = documentUpload.files[0];
              const fileExtension = file.name.substring(file.name.length - 4);
              switch (fileExtension) {
                case '.kml':
                  this.kmlForestryUploadService.parseKmlDocument(file, false).then(parsedKml => {
                    this.createParcel(parsedKml);
                  });
                  break;
                case '.kmz':
                  this.kmlForestryUploadService.parseKmzDocument(file, false).then(parsedKml => {
                    this.createParcel(parsedKml);
                  });
                  break;
                default:
                  this.toastService.sendMessage('Only .kml and .kmz files are supported.', ToastLevel.WARN);
              }
              this.documentUpload.nativeElement.value = '';
            }
          };
          documentUpload.click();
        }
      },
    });
  }

  private createParcel(parsedKml: any) {
    const multiPolygonCoordinates = parsedKml.features.map(feature => feature.geometry.coordinates);
    const multiPolygonFeature = turf.truncate(
      turf.multiPolygon(multiPolygonCoordinates, {
        name: 'Merged MultiPolygon',
      }),
      { coordinates: 2 }
    );
    let newParcel = new Parcel({
      parcelNumber: '[CUSTOM PARCEL]',
      feature: multiPolygonFeature,
      stands: [],
      llUuid: uuidv4().toLowerCase(),
      svg: this.gisService.getGeomSVG(multiPolygonFeature),
    });
    this.selectParcelByParcel(newParcel);
  }
}
